import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete
} from '@mui/material'
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material'
import { nomenclatorV2Api, TechniquePayload } from '../../services/nomenclatorV2Api'

interface Technique {
  id: string
  category: string
  display_name_en: string
  display_name_ro: string
  applicable_modalities?: string[]
  description?: string
}

const TECHNIQUE_CATEGORIES = ['contrast', 'protocol', 'sequence', 'other']
const MODALITIES = ['CT', 'MR', 'DX', 'US', 'NM', 'PT', 'MG']

export const TechniquesManager: React.FC = () => {
  const [techniques, setTechniques] = useState<Technique[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [techniqueToDelete, setTechniqueToDelete] = useState<string | null>(null)

  const [form, setForm] = useState<TechniquePayload>({
    id: '',
    category: '',
    display_name_en: '',
    display_name_ro: '',
    applicable_modalities: [],
    description: ''
  })

  const loadTechniques = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await nomenclatorV2Api.listTechniques()
      setTechniques(data)
    } catch (err) {
      console.error('Failed to load techniques', err)
      setError('Nu am putut încărca tehnicile')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTechniques()
  }, [])

  const handleSubmit = async () => {
    if (!form.id || !form.display_name_ro || !form.category) {
      setError('ID, Nume RO și Categorie sunt obligatorii')
      return
    }

    try {
      if (editingId) {
        await nomenclatorV2Api.updateTechnique(editingId, {
          category: form.category,
          display_name_en: form.display_name_en,
          display_name_ro: form.display_name_ro,
          applicable_modalities: form.applicable_modalities,
          description: form.description
        })
        setSuccess('Tehnică actualizată cu succes')
      } else {
        await nomenclatorV2Api.createTechnique({
          id: form.id,
          category: form.category,
          display_name_en: form.display_name_en,
          display_name_ro: form.display_name_ro,
          applicable_modalities: form.applicable_modalities,
          description: form.description
        })
        setSuccess('Tehnică adăugată cu succes')
      }
      resetForm()
      loadTechniques()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la salvare')
    }
  }

  const handleEdit = (technique: Technique) => {
    setForm({
      id: technique.id,
      category: technique.category,
      display_name_en: technique.display_name_en,
      display_name_ro: technique.display_name_ro,
      applicable_modalities: technique.applicable_modalities || [],
      description: technique.description || ''
    })
    setEditingId(technique.id)
  }

  const handleDeleteClick = (id: string) => {
    setTechniqueToDelete(id)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!techniqueToDelete) return

    try {
      await nomenclatorV2Api.deleteTechnique(techniqueToDelete)
      setSuccess('Tehnică ștearsă cu succes')
      loadTechniques()
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Eroare la ștergere')
    } finally {
      setDeleteDialogOpen(false)
      setTechniqueToDelete(null)
    }
  }

  const resetForm = () => {
    setForm({
      id: '',
      category: '',
      display_name_en: '',
      display_name_ro: '',
      applicable_modalities: [],
      description: ''
    })
    setEditingId(null)
  }

  if (loading && techniques.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    )
  }

  // Compact input styles
  const inputSx = {
    '& .MuiInputBase-root': { fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { fontSize: '0.75rem' }
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1, py: 0.5, fontSize: '0.78rem' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess(null)} message={success} />

      {/* Form - Compact */}
      <Card sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <AddIcon color="primary" sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {editingId ? 'Editează Tehnică' : 'Adaugă Tehnică Nouă'}
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" gap={1} flexWrap="wrap">
              <TextField
                label="Cod (EN)"
                value={form.id}
                onChange={(e) => setForm({ ...form, id: e.target.value })}
                disabled={!!editingId}
                required
                size="small"
                sx={{ width: 140, ...inputSx }}
              />
              <FormControl required size="small" sx={{ minWidth: 140, ...inputSx }}>
                <InputLabel>Categorie</InputLabel>
                <Select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  label="Categorie"
                >
                  {TECHNIQUE_CATEGORIES.map((cat) => (
                    <MenuItem key={cat} value={cat} sx={{ fontSize: '0.8rem' }}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Nume RO"
                value={form.display_name_ro}
                onChange={(e) => setForm({ ...form, display_name_ro: e.target.value })}
                required
                size="small"
                sx={{ flex: 1, minWidth: 150, ...inputSx }}
              />
              <TextField
                label="Nume EN"
                value={form.display_name_en}
                onChange={(e) => setForm({ ...form, display_name_en: e.target.value })}
                size="small"
                sx={{ flex: 1, minWidth: 150, ...inputSx }}
              />
            </Box>

            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <Autocomplete
                multiple
                size="small"
                options={MODALITIES}
                value={form.applicable_modalities || []}
                onChange={(_, newValue) => setForm({ ...form, applicable_modalities: newValue })}
                renderInput={(params) => <TextField {...params} label="Modalități" size="small" />}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip label={option} {...getTagProps({ index })} key={option} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                  ))
                }
                sx={{ minWidth: 200, flex: 1, ...inputSx }}
              />
              <TextField
                label="Descriere"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                size="small"
                sx={{ flex: 2, minWidth: 200, ...inputSx }}
              />
              <Button 
                variant="contained" 
                onClick={handleSubmit} 
                disabled={!form.id || !form.display_name_ro || !form.category}
                size="small"
                sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}
              >
                {editingId ? 'Salvează' : 'Adaugă'}
              </Button>
              {editingId && (
                <Button variant="outlined" onClick={resetForm} size="small" sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none' }}>
                  Anulează
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Table - Compact */}
      <Card sx={{
        '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.78rem' },
        '& .MuiTableBody-root .MuiIconButton-root': { p: 0.25 },
        '& .MuiTableBody-root .MuiSvgIcon-root': { fontSize: '1rem' }
      }}>
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
            Tehnici ({techniques.length})
          </Typography>

          <Table size="small">
            <TableHead>
              <TableRow sx={{
                bgcolor: 'grey.900',
                '& th': { color: 'common.white', fontSize: '0.75rem', fontWeight: 600, py: 0.5, px: 1 }
              }}>
                <TableCell>Cod</TableCell>
                <TableCell>Categorie</TableCell>
                <TableCell>Nume RO</TableCell>
                <TableCell>Nume EN</TableCell>
                <TableCell>Modalități</TableCell>
                <TableCell align="right">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {techniques.map((technique) => (
                <TableRow key={technique.id} hover>
                  <TableCell><strong>{technique.id}</strong></TableCell>
                  <TableCell>{technique.category}</TableCell>
                  <TableCell>{technique.display_name_ro}</TableCell>
                  <TableCell>{technique.display_name_en}</TableCell>
                  <TableCell>
                    {technique.applicable_modalities && technique.applicable_modalities.length > 0 ? (
                      <Box display="flex" gap={0.25} flexWrap="wrap">
                        {technique.applicable_modalities.slice(0, 4).map((mod) => (
                          <Chip key={mod} label={mod} size="small" sx={{ height: 18, fontSize: '0.68rem' }} />
                        ))}
                        {technique.applicable_modalities.length > 4 && (
                          <Chip label={`+${technique.applicable_modalities.length - 4}`} size="small" sx={{ height: 18, fontSize: '0.68rem' }} />
                        )}
                      </Box>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(technique)} color="primary"><EditIcon /></IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(technique.id)} color="error"><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Dialog - Compact */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { bgcolor: 'background.paper', backgroundImage: 'none' } }}>
        <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 600, borderBottom: '1px solid', borderColor: 'divider' }}>
          Confirmare ștergere
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography sx={{ fontSize: '0.85rem' }}>Sigur vrei să ștergi tehnica <strong>{techniqueToDelete}</strong>?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setDeleteDialogOpen(false)} size="small" sx={{ fontSize: '0.8rem' }}>Anulează</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" size="small" sx={{ fontSize: '0.8rem' }}>Șterge</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

